// src/lib/AuthContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithCredential,
  GoogleAuthProvider,
  OAuthProvider,
  signOut,
  onAuthStateChanged,
  updateProfile
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { auth, db, functions } from "./firebase";
import { isAdminEmail } from "./admin";

const AuthContext = createContext();
const googleProvider = new GoogleAuthProvider();

const LINKEDIN_PROVIDER_ID = "oidc.linkedin";
const LINKEDIN_SCOPES = "openid profile email";
const LINKEDIN_AUTH_URL = "https://www.linkedin.com/oauth/v2/authorization";
const LINKEDIN_CALLBACK_PATH = "/auth/linkedin/callback";
const LINKEDIN_STATE_KEY = "linkedin_oauth_state";
const LINKEDIN_ROLE_KEY = "linkedin_oauth_role";
const LINKEDIN_ORG_KEY = "linkedin_oauth_org";
/** Empty / pending sentinel — profile must not be created until role is chosen. */
const LINKEDIN_ROLE_PENDING = "__pending__";
const LINKEDIN_CLIENT_ID =
  process.env.REACT_APP_LINKEDIN_CLIENT_ID || "78vyc71w8gqgr9";

export function useAuth() {
  return useContext(AuthContext);
}

/** Map UI / alias roles onto stored app roles. */
function normalizeAppRole(role) {
  if (role === "corporation" || role === "business_owner") return "corporation";
  return "startup";
}

function isConcreteRole(role) {
  return role === "startup" || role === "corporation" || role === "business_owner";
}

function detectAuthProvider(user) {
  const id = user?.providerData?.[0]?.providerId || "";
  if (id.includes("linkedin") || id === LINKEDIN_PROVIDER_ID) return "linkedin";
  if (id === "google.com") return "google";
  return "email";
}

function resolveEffectiveRole(userProfile, isAdmin, viewMode) {
  if (!isAdmin) {
    if (userProfile?.role === "startup" || userProfile?.role === "corporation") {
      return userProfile.role;
    }
    return "startup";
  }

  // Admins can preview every side of the platform
  if (viewMode === "startup") return "startup";
  if (viewMode === "corporation") return "corporation";
  if (viewMode === "user") {
    // Legacy value from older sessions
    return userProfile?.role === "corporation" ? "corporation" : "startup";
  }
  return "admin";
}

function linkedInRedirectUri() {
  return `${window.location.origin}${LINKEDIN_CALLBACK_PATH}`;
}

function clearLinkedInOAuthSession() {
  sessionStorage.removeItem(LINKEDIN_STATE_KEY);
  sessionStorage.removeItem(LINKEDIN_ROLE_KEY);
  sessionStorage.removeItem(LINKEDIN_ORG_KEY);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("admin");

  const isAdmin = isAdminEmail(currentUser?.email);
  const needsOnboarding = Boolean(currentUser && !userProfile && !loading);
  const effectiveRole = useMemo(
    () => resolveEffectiveRole(userProfile, isAdmin, viewMode),
    [userProfile, isAdmin, viewMode]
  );

  /**
   * Load or optionally create `users/{uid}`.
   * When createIfMissing is false and no doc exists, returns needsOnboarding
   * without writing a default Startup profile.
   */
  async function ensureUserDocument(user, { role, orgName, authProvider, createIfMissing = true }) {
    const userRef = doc(db, "users", user.uid);
    const snap = await getDoc(userRef);

    if (snap.exists()) {
      const existing = snap.data();
      setUserProfile(existing);
      return { isNewUser: false, needsOnboarding: false, profile: existing };
    }

    if (!createIfMissing || !isConcreteRole(role)) {
      setUserProfile(null);
      return { isNewUser: true, needsOnboarding: true, profile: null };
    }

    const profile = {
      email: user.email || "",
      displayName: user.displayName || "",
      photoURL: user.photoURL || "",
      role: normalizeAppRole(role),
      orgName: (orgName || "").trim(),
      createdAt: new Date().toISOString(),
      authProvider: authProvider || detectAuthProvider(user),
    };
    await setDoc(userRef, profile);
    setUserProfile(profile);
    return { isNewUser: true, needsOnboarding: false, profile };
  }

  /** Finish account setup after OAuth when role was not chosen yet. */
  async function completeOnboarding({ role, orgName }) {
    const user = auth.currentUser;
    if (!user) {
      const err = new Error("You must be signed in to finish setup.");
      err.code = "auth/not-signed-in";
      throw err;
    }
    if (!isConcreteRole(role)) {
      const err = new Error("Please choose Startup or Business Owner.");
      err.code = "onboarding/role-required";
      throw err;
    }
    if (!String(orgName || "").trim()) {
      const err = new Error("Organization name is required.");
      err.code = "onboarding/org-required";
      throw err;
    }
    return ensureUserDocument(user, {
      role,
      orgName: String(orgName).trim(),
      authProvider: detectAuthProvider(user),
      createIfMissing: true,
    });
  }

  async function signup(email, password, displayName, role, orgName) {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(result.user, { displayName });
    const profile = {
      email,
      displayName,
      role: normalizeAppRole(role),
      orgName,
      createdAt: new Date().toISOString(),
    };
    await setDoc(doc(db, "users", result.user.uid), profile);
    setUserProfile(profile);
    return result;
  }

  async function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  /**
   * Google sign-in / sign-up.
   * Pass a concrete role to create the profile immediately (signup path).
   * Omit role / pass null to defer creation until completeOnboarding (login path).
   */
  async function loginWithGoogle(role, orgName) {
    const result = await signInWithPopup(auth, googleProvider);
    const createIfMissing = isConcreteRole(role);
    const outcome = await ensureUserDocument(result.user, {
      role,
      orgName,
      authProvider: "google",
      createIfMissing,
    });
    return { result, ...outcome };
  }

  /**
   * Manual LinkedIn OIDC (authorization-code) flow.
   * Redirects the browser to LinkedIn; completion happens on /auth/linkedin/callback.
   * Pass null/undefined role from Sign In so new users pick a role after redirect.
   */
  async function loginWithLinkedIn(role, orgName) {
    const clientId = LINKEDIN_CLIENT_ID;
    if (!clientId) {
      throw new Error(
        "Missing LinkedIn client ID. Set REACT_APP_LINKEDIN_CLIENT_ID or update the fallback in AuthContext.js."
      );
    }

    const state =
      (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    sessionStorage.setItem(LINKEDIN_STATE_KEY, state);
    sessionStorage.setItem(
      LINKEDIN_ROLE_KEY,
      isConcreteRole(role) ? role : LINKEDIN_ROLE_PENDING
    );
    sessionStorage.setItem(LINKEDIN_ORG_KEY, orgName || "");

    const params = new URLSearchParams({
      response_type: "code",
      client_id: clientId,
      redirect_uri: linkedInRedirectUri(),
      scope: LINKEDIN_SCOPES,
      state,
    });

    window.location.assign(`${LINKEDIN_AUTH_URL}?${params.toString()}`);
  }

  /**
   * Finish LinkedIn sign-in after LinkedIn redirects back with ?code=.
   * Calls Cloud Function to exchange code → id_token, then signs into Firebase.
   */
  async function completeLinkedInSignIn({ code, state }) {
    const expectedState = sessionStorage.getItem(LINKEDIN_STATE_KEY);
    const roleRaw = sessionStorage.getItem(LINKEDIN_ROLE_KEY);
    const orgName = sessionStorage.getItem(LINKEDIN_ORG_KEY) || "";
    const role = roleRaw === LINKEDIN_ROLE_PENDING ? null : roleRaw;

    if (!code) {
      const err = new Error("Missing LinkedIn authorization code.");
      err.code = "linkedin/missing-code";
      throw err;
    }
    if (!expectedState || !state || state !== expectedState) {
      const err = new Error("LinkedIn sign-in state mismatch. Please try again.");
      err.code = "linkedin/invalid-state";
      throw err;
    }

    const redirectUri = linkedInRedirectUri();
    const exchangeLinkedInCode = httpsCallable(functions, "exchangeLinkedInCode");
    const { data } = await exchangeLinkedInCode({ code, redirectUri });

    if (!data?.idToken) {
      const err = new Error("LinkedIn token exchange did not return an id_token.");
      err.code = "linkedin/missing-id-token";
      throw err;
    }

    const provider = new OAuthProvider(LINKEDIN_PROVIDER_ID);
    const credential = provider.credential({
      idToken: data.idToken,
      accessToken: data.accessToken || undefined,
    });

    const result = await signInWithCredential(auth, credential);
    clearLinkedInOAuthSession();

    const createIfMissing = isConcreteRole(role);
    const outcome = await ensureUserDocument(result.user, {
      role,
      orgName,
      authProvider: "linkedin",
      createIfMissing,
    });

    return { result, ...outcome };
  }

  async function logout() {
    await signOut(auth);
    setUserProfile(null);
    setViewMode("admin");
  }

  async function fetchProfile(uid) {
    const snap = await getDoc(doc(db, "users", uid));
    if (snap.exists()) setUserProfile(snap.data());
    else setUserProfile(null);
  }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await fetchProfile(user.uid);
        // Admins default into admin view on each session start
        if (isAdminEmail(user.email)) setViewMode("admin");
      } else {
        setUserProfile(null);
        setViewMode("admin");
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  return (
    <AuthContext.Provider value={{
      currentUser,
      userProfile,
      needsOnboarding,
      signup,
      login,
      loginWithGoogle,
      loginWithLinkedIn,
      completeLinkedInSignIn,
      completeOnboarding,
      logout,
      loading,
      isAdmin,
      viewMode,
      setViewMode,
      effectiveRole,
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
